import usePersistFn from '@/hooks/usePersistFn';
import { useDocsDataModelStore } from '@/models/docs-data/docsData';
import { useEnvConfigModelStore } from '@/models/env-config/envConfig';
import { DocsSiteVisitType } from '@/shared/constant/DocsIdTypeData';
import { OnlineType } from '@/shared/constant/OnlineType';
import { customDomainRequestPath } from '@/shared/request/customDomainRequestPath';
import { DataSchemaContext as MarkdownSchemaContext } from '@/ui-kit/components/MarkdownSafeMdxViewer/Components/DataSchema/DataSchemaContext';
import InnerAsyncMarkdownViewer from '@/ui-kit/components/MarkdownSafeMdxViewer/viewer';
import type { IDataSchemaDefinitions } from '@tarslib/types/lib/dataSchema';
import clsx from 'clsx';
import trim from 'lodash-es/trim';
import React from 'react';
import styles from './MarkdownViewer.module.css';
import useAppApiServerHostnames from './hooks/useAppApiServerHostname';

export type MarkdownViewerProps = {
  value: string;
  enableHeaderPlugin: boolean;
  inDocView: boolean;
  className?: string;
};

export function MarkdownViewer(props: MarkdownViewerProps) {
  const { value, enableHeaderPlugin, inDocView, className } = props;

  const locale = useDocsDataModelStore((x) => x.docsBaseConfig.language);
  const onlineType = useDocsDataModelStore((x) => x.docsIdTypeData.onlineType);
  const appLowerName = useEnvConfigModelStore((x) => x.APP_NAME_LOWER);
  const appApiServerHostnames = useAppApiServerHostnames();
  const dataSchemaDefinitionMap = useDocsDataModelStore(
    (x) => x.resourceData.extraData.dataSchemaDefinitions,
  );
  const docsIdTypeData = useDocsDataModelStore((x) => x.docsIdTypeData);

  const imageReplaceHostOptions = React.useMemo(() => {
    return {
      searchHostname: appApiServerHostnames,
      replaceHostname: '',
      // 支持自定义域名图片替换
      customDomainRequestPath: (() => {
        if (
          docsIdTypeData.onlineType === OnlineType.DocsSite &&
          docsIdTypeData.visitType === DocsSiteVisitType.CustomDomain
        ) {
          const urlPrefix = (docsIdTypeData.subdirectory + customDomainRequestPath).trim();
          if (urlPrefix) {
            return '/' + trim(urlPrefix, '/');
          }
          return urlPrefix;
        }
        return '';
      })(),
      isEnterpriseEdition: IS_PDV,
    };
  }, [appApiServerHostnames, docsIdTypeData]);

  const imageSrcAddParams = React.useMemo(() => {
    if (IS_PDV) {
      return undefined;
    }

    return {
      searchHostnames: appApiServerHostnames,
      onlineType,
      locale: locale,
    };
  }, [appApiServerHostnames, locale, onlineType]);

  const urlToCurrentDocUrl = usePersistFn((url: string) => url);

  const markdownSchemaContextValue = React.useMemo(() => {
    return (dataSchemaDefinitionMap || {}) as IDataSchemaDefinitions;
  }, [dataSchemaDefinitionMap]);

  if (!value) {
    return null;
  }
  return (
    <div className={clsx(styles['markdown-container'], className)}>
      <MarkdownSchemaContext.Provider value={markdownSchemaContextValue}>
        <InnerAsyncMarkdownViewer
          value={value}
          renderExternalLink={urlToCurrentDocUrl}
          openExternalLink={undefined}
          imageReplaceHostOptions={imageReplaceHostOptions}
          imageSrcAddParams={imageSrcAddParams}
          showErrorMsg={false}
          locale={locale}
          enableDataSchema
          enableHeadingAutoLink={enableHeaderPlugin}
          enableHeadingSlugId={enableHeaderPlugin}
          inDocView={inDocView}
          slugIdPrefix=""
          appLowerName={appLowerName}
        />
      </MarkdownSchemaContext.Provider>
    </div>
  );
}
